// Applications domain types for Career Assistant v5

export type ApplicationStatus = 'applied' | 'interviewing' | 'offered' | 'rejected' | 'withdrawn';
export type ApplicationBucket = 'to_apply' | 'applied' | 'interviewing' | 'offered' | 'rejected';

export interface Application {
  id: string;
  job_id: string;
  user_id: string;
  
  // Job details (denormalized for quick access)
  job_title: string;
  company: string;
  location: string;
  job_url?: string;
  salary_range?: string;
  
  // Application details
  status: ApplicationStatus;
  bucket: ApplicationBucket;
  applied_at: string;
  updated_at: string;
  created_at: string;
  
  // Application content
  cover_letter?: string;
  notes?: string;
  resume_version?: string;
  resume_id?: string;
  
  // Tracking and metadata
  source: 'manual' | 'auto_apply' | 'import';
  source_url?: string;
  external_id?: string;
  
  // Workflow
  timeline?: ApplicationTimelineEvent[];
  next_action?: string;
  next_action_date?: string;
  
  // Ratings and feedback
  company_rating?: number;
  job_rating?: number;
  experience_notes?: string;
  
  // Auto-apply specific
  auto_applied?: boolean;
  auto_apply_rule_id?: string;
  auto_apply_log?: string[];
}

export interface ApplicationTimelineEvent {
  id: string;
  application_id: string;
  event_type: 'applied' | 'viewed' | 'response' | 'interview_scheduled' | 'interview_completed' | 'status_change' | 'note_added' | 'follow_up';
  event_date: string;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface ApplicationsResponse {
  applications: Application[];
  total_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
  status_counts: Record<ApplicationStatus, number>;
  bucket_counts: Record<ApplicationBucket, number>;
}

export interface ApplicationFilters {
  status?: ApplicationStatus[];
  bucket?: ApplicationBucket[];
  company?: string;
  location?: string;
  salary_min?: number;
  salary_max?: number;
  applied_after?: string;
  applied_before?: string;
  source?: Application['source'][];
  search?: string;
}

export interface ApplicationSearchRequest {
  filters?: ApplicationFilters;
  page?: number;
  pageSize?: number;
  sort?: 'applied_date' | 'updated_date' | 'company' | 'job_title' | 'status';
  order?: 'asc' | 'desc';
}

export interface ApplicationCreateRequest {
  job_id: string;
  status?: ApplicationStatus;
  bucket?: ApplicationBucket;
  cover_letter?: string;
  notes?: string;
  resume_id?: string;
  source?: Application['source'];
  source_url?: string;
}

export interface ApplicationUpdateRequest {
  status?: ApplicationStatus;
  bucket?: ApplicationBucket;
  cover_letter?: string;
  notes?: string;
  next_action?: string;
  next_action_date?: string;
  company_rating?: number;
  job_rating?: number;
  experience_notes?: string;
}

export interface ApplicationStats {
  total_applications: number;
  status_breakdown: Record<ApplicationStatus, number>;
  bucket_breakdown: Record<ApplicationBucket, number>;
  monthly_applications: Array<{
    month: string;
    count: number;
  }>;
  response_rate: number;
  interview_rate: number;
  offer_rate: number;
  avg_response_time_days: number;
  top_companies: Array<{
    company: string;
    count: number;
  }>;
  top_locations: Array<{
    location: string;
    count: number;
  }>;
}

// UI-specific mappings and constants
export const APPLICATION_STATUS_CONFIG: Record<ApplicationStatus, {
  label: string;
  color: 'blue' | 'amber' | 'green' | 'red' | 'gray';
  description: string;
}> = {
  applied: {
    label: 'Applied',
    color: 'blue',
    description: 'Application submitted, awaiting response'
  },
  interviewing: {
    label: 'Interviewing',
    color: 'amber',
    description: 'Interview process in progress'
  },
  offered: {
    label: 'Offered',
    color: 'green',
    description: 'Job offer received'
  },
  rejected: {
    label: 'Rejected',
    color: 'red',
    description: 'Application was rejected'
  },
  withdrawn: {
    label: 'Withdrawn',
    color: 'gray',
    description: 'Application withdrawn by candidate'
  }
};

export const APPLICATION_BUCKET_CONFIG: Record<ApplicationBucket, {
  label: string;
  description: string;
}> = {
  to_apply: {
    label: 'To Apply',
    description: 'Jobs saved for future application'
  },
  applied: {
    label: 'Applied',
    description: 'Applications submitted'
  },
  interviewing: {
    label: 'Interviewing',
    description: 'Currently in interview process'
  },
  offered: {
    label: 'Offers',
    description: 'Job offers received'
  },
  rejected: {
    label: 'Rejected',
    description: 'Unsuccessful applications'
  }
};

export const APPLICATION_SOURCES: Record<Application['source'], string> = {
  manual: 'Manual Entry',
  auto_apply: 'Auto-Apply',
  import: 'Import'
};